import React, { useState } from 'react';
import { Button, Modal } from 'equasion-lib';
import { FiMaximize } from 'react-icons/fi';
import PaginatedZoneSelect from '@/lib/shared-coms/PaginatedZoneSelect/PaginatedZoneSelect';
import ZoneScanOverlay from '@/lib/shared-coms/ZoneScanner/ZoneScanOverlay';
import type { ScannedZone } from '@/lib/shared-coms/ZoneScanner/ZoneScanOverlay';
import { useScanner } from '@/root/App/com/ScannerProvider/ScannerProvider';
import '@/lib/shared-coms/ZoneScanner/ZoneScanner.scss';
import './ZonePickerModal.scss';

export type ChosenZone = {
  id: string;
  /** Bare zone name. */
  name: string;
  /** Name as shown to the operator — carries the " (Quarantine)" marker. */
  display: string;
  zoneType?: string;
};

export type ZonePickerView = 'scanner' | 'list';

const QUARANTINE_SUFFIX = / \(Quarantine\)$/;

const displayFor = (zone: ScannedZone) =>
  zone.zoneType === 'quarantine' ? `${zone.name} (Quarantine)` : zone.name;

interface ZonePickerModalProps {
  open: boolean;
  /** Cancel: closing the list (and the scanner's X, unless `onScannerClose` says otherwise). */
  onClose: () => void;
  /**
   * The scanner's X, when it should do more than cancel — a scanner that opened with its screen
   * leaves the screen (useZoneScanOnLoad). Never opens the list. Defaults to `onClose`.
   */
  onScannerClose?: () => void;
  /** Fired once a zone is settled — by a scan, or by confirming a dropdown pick. */
  onZoneChosen: (zone: ChosenZone) => void;
  companyId: string;
  locationId?: string;
  zoneTypes?: string[];
  excludeZoneIds?: string[];
  /** Shown when a scan lands on a zone in `excludeZoneIds`. */
  excludeMessage?: string;
  /** Line above the list, e.g. "Select a destination zone". */
  title?: string;
  label?: string;
  confirmLabel?: string;
  /** The scanner's heading: what the zone is for ("Scan destination zone"). */
  scanTitle?: string;
  /** Under the heading: which item it applies to. The scanner hides the screen behind it. */
  scanHint?: string;
  /**
   * Where the picker opens. Defaults to the scanner, and to the list only where the camera is
   * blocked (scanFirst). A screen whose own camera is already live behind the picker —
   * so the operator reached it by asking for the list — passes 'list'.
   */
  initialView?: ZonePickerView;
  className?: string;
}

/**
 * The one place the warehouse app asks "which zone?" outside of a larger form.
 *
 * On every device it opens straight onto the full-screen zone scanner: scanning the rack
 * label settles the zone in a single action, because that is the motion an operator actually
 * performs at the shelf. "Choose … from the list" switches to the type-ahead list, which keeps its
 * confirm press so a mis-tap cannot silently commit, and has a way back to the scanner. The X on
 * either cancels back to the screen. Where the camera is blocked the list is where it starts.
 */
const ZonePickerModal = ({
  open,
  onClose,
  onScannerClose,
  onZoneChosen,
  companyId,
  locationId,
  zoneTypes,
  excludeZoneIds = [],
  excludeMessage,
  title = 'Select a zone',
  label = 'Zone',
  confirmLabel = 'Set Zone',
  scanTitle,
  scanHint,
  initialView,
  className = 'zone-picker-modal'
}: ZonePickerModalProps) => {
  const { scanFirst } = useScanner();
  const startView: ZonePickerView = initialView || (scanFirst ? 'scanner' : 'list');
  const [view, setView] = useState<ZonePickerView>(startView);
  const [staged, setStaged] = useState<ChosenZone | null>(null);
  const [knownZones, setKnownZones] = useState<ScannedZone[]>([]);

  // Each opening starts clean, and on the view it should start on. Done while rendering rather
  // than in an effect, so an opening never mounts the previous view's scanner for one frame.
  // Without the reset the list would re-open showing the zone picked the last time it was used —
  // on the move/quarantine screens that is a *different* field's zone.
  const [openedFor, setOpenedFor] = useState(open);
  if (open !== openedFor) {
    setOpenedFor(open);
    if (open) {
      setView(startView);
      setStaged(null);
    }
  }

  // The camera turned out to be blocked just after the picker opened on the scanner (the
  // browser's permission answer arrives a moment after the screen loads): fall back to the list
  // rather than leave the operator on a scanner that cannot see. Only on that change — an operator
  // who later asks for the scanner from the list gets it.
  const [scanFirstSeen, setScanFirstSeen] = useState(scanFirst);
  if (scanFirst !== scanFirstSeen) {
    setScanFirstSeen(scanFirst);
    if (!scanFirst && open && view === 'scanner') setView('list');
  }

  const choose = (zone: ChosenZone) => {
    onZoneChosen(zone);
    onClose();
  };

  const listLabel = `Choose ${label.toLowerCase()} from the list`;

  return (
    <>
      {open && view === 'scanner' && (
        <ZoneScanOverlay
          companyId={companyId}
          locationId={locationId}
          zoneTypes={zoneTypes}
          excludeZoneIds={excludeZoneIds}
          excludeMessage={excludeMessage}
          title={scanTitle || `Scan ${label}`}
          hint={scanHint}
          manualEntryLabel={listLabel}
          onManualEntry={() => setView('list')}
          onClose={onScannerClose || onClose}
          onSelect={(zone) => {
            setKnownZones((zones) =>
              zones.some(({ id }) => id === zone.id) ? zones : [...zones, zone]
            );
            choose({
              id: zone.id,
              name: zone.name,
              display: displayFor(zone),
              zoneType: zone.zoneType
            });
          }}
        />
      )}
      <Modal
        className={className}
        modalOpen={open && view === 'list'}
        setModalOpen={(nextOpen?: boolean) => {
          if (!nextOpen) onClose();
        }}
      >
        <div className="zone-picker">
          <div className="zone-picker__title">{title}</div>

          <PaginatedZoneSelect
            form={false}
            label={label}
            companyId={companyId}
            locationId={locationId}
            zoneTypes={zoneTypes}
            excludeZoneIds={excludeZoneIds}
            value={staged?.id || ''}
            knownZones={knownZones}
            onChange={({ value, display, zoneType }) =>
              setStaged({
                id: value,
                name: display.replace(QUARANTINE_SUFFIX, ''),
                display,
                zoneType
              })
            }
          />

          <Button
            type="primary"
            fullWidth
            disabled={!staged?.id}
            onClick={() => staged && choose(staged)}
          >
            {confirmLabel}
          </Button>

          <div className="zone-picker__divider">
            <span>or scan the zone label</span>
          </div>

          <button
            type="button"
            className="zone-scan-trigger zone-scan-trigger--block"
            onClick={() => setView('scanner')}
          >
            <FiMaximize aria-hidden />
            <span className="zone-scan-trigger__label">
              {scanFirst ? 'Back to the scanner' : 'Scan zone label'}
            </span>
          </button>
        </div>
      </Modal>
    </>
  );
};

export default ZonePickerModal;
